import { Component, Input, OnChanges, OnInit, OnDestroy, Output, EventEmitter, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';

import { BackgroundInfoSubTopic, Message, BackgroundInfoType } from 'src/app/shared/models';
import { TranslationConstants, TinyTextEditor } from 'src/app/shared/constants';

import { ModalService, ConfirmationModalComponent } from 'src/app/shared/modules/modal';
import { BackgroundInfoService } from 'src/app/shared/services';

@Component({
  selector: 'background-info-gallery',
  templateUrl: './background-info-gallery.component.html',
})
export class BackgroundInfoGalleryComponent implements OnChanges, OnInit, OnDestroy {
  @Input() subTopics: BackgroundInfoSubTopic[];
  @Input() activeJurisdictionId: number;
  @Input() useCase: BackgroundInfoType;
  @Input() resourceId: number;
  @Output() selectTopic = new EventEmitter();
  @Output() preventPageChangeDueToUnSavedWork = new EventEmitter();

  activeSubTopicIndex = 0;
  activeSubTopic: BackgroundInfoSubTopic;
  cloneOfActiveSubTopic: BackgroundInfoSubTopic;

  tinyInit = TinyTextEditor.INIT;

  private _subscriptions: Subscription[] = [];

  constructor(
    protected modalService: ModalService,
    private _backgroundInfoService: BackgroundInfoService,
    private _ngZone: NgZone,
  ) {
  }

  ngOnInit(): void {
    this.listenToAddNewSubtopicEvent();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  ngOnChanges({ subTopics }: any): void {
    if (this.shouldResetSubtopicIndex(subTopics)) {
      this.activeSubTopicIndex = 0;
      this._backgroundInfoService.setCurrentSubtopicIndex(0);
    }
    this.setSubTopic();
  }

  selectSubTopic(shouldIncreaseIndex: boolean, skipCheck: boolean = false): void {
    if (this.checkIfSubTopicChanged() && !skipCheck) {
      return this.showSubTopicEditedModal(shouldIncreaseIndex);
    }
    this.changeActiveTopicIndex(shouldIncreaseIndex);
    this.setSubTopic();
  }

  isSubTopicSelected(indexOfIndicator: number): boolean {
    return this.activeSubTopicIndex === indexOfIndicator;
  }

  emitModelChange(): void {
    this.preventPageChangeDueToUnSavedWork.emit(this.checkIfSubTopicChanged());
  }

  checkIfSubTopicChanged(): boolean {
    return JSON.stringify(this.cloneOfActiveSubTopic) !== JSON.stringify(this.activeSubTopic);
  }

  save() {
    this._backgroundInfoService
      .updateSubTopic(this.activeSubTopic, this.resourceId, this.useCase)
      .subscribe(() => {
        this.selectTopic.emit();
      });
    this.preventPageChangeDueToUnSavedWork.emit(false);
  }

  isFirstSubTopic(): boolean {
    return this.activeSubTopicIndex === 0;
  }

  isLastSubTopic(): boolean {
    return this.activeSubTopicIndex === this.subTopics.length - 1;
  }

  selectSubTopicByIndex(index: number): void {
    this.activeSubTopicIndex = index;
    this._backgroundInfoService.setCurrentSubtopicIndex(index);
    this.setSubTopic();
  }

  private changeActiveTopicIndex(shouldIncreaseIndex: boolean): void {
    shouldIncreaseIndex ? this.activeSubTopicIndex += 1 : this.activeSubTopicIndex -= 1;
    if (this.activeSubTopicIndex >= this.subTopics.length) {
      this.activeSubTopicIndex = this.subTopics.length - 1;
    } else if (this.activeSubTopicIndex < 0) {
      this.activeSubTopicIndex = 0;
    }

    this._backgroundInfoService.setCurrentSubtopicIndex(this.activeSubTopicIndex);
  }

  private showSubTopicEditedModal(shouldIncreaseIndex: boolean): void {
    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.GLOBAL_LEAVE,
        content: TranslationConstants.GLOBAL_LEAVE_CONTENT,
      } as Message,
      success: () => {
        this.subTopics[this.activeSubTopicIndex] = { ...{}, ...this.cloneOfActiveSubTopic };
        this.selectSubTopic(shouldIncreaseIndex, true);
        this.emitModelChange();
      },
    });
  }

  private listenToAddNewSubtopicEvent(): void {
    this._subscriptions.push(
      this._backgroundInfoService.subTopicIndexChanged$.subscribe((currentIndex: number) => {
        this.activeSubTopicIndex = currentIndex;
        this.setSubTopic();
      })
    );
  }

  private setSubTopic(): void {
    this.activeSubTopic = this.subTopics[this.activeSubTopicIndex];
    this.cloneOfActiveSubTopic = { ...{}, ...this.activeSubTopic };
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.cloneOfActiveSubTopic.discussion = this.activeSubTopic.discussion;
        this.preventPageChangeDueToUnSavedWork.emit(false);
      });
    });
  }

  private shouldResetSubtopicIndex(subTopics: any): boolean {
    if (subTopics && subTopics.currentValue && subTopics.previousValue && subTopics.currentValue[0] && subTopics.previousValue[0]) {
      return subTopics.currentValue[0].backgroundInfoId !== subTopics.previousValue[0].backgroundInfoId;
    }
    return false;
  }
}
